import { useTranslation } from 'react-i18next';

import { Icon, RadioBtn, UiButton, UiText, UITooltip } from '@shared/ui';

import cls from './HintsSettings.module.css';
import { useProfilePreferences } from '../../model/useProfilePreferences';

import type { Profile } from '@entities/profile';

type HintsSettingsProps = {
    profile: Profile;
};

export const HintsSettings = ({ profile }: HintsSettingsProps) => {
    const { t } = useTranslation();
    const { showHints, changeShowHints, isPending } = useProfilePreferences(profile);

    return (
        <section className={cls.settingsSection}>
            <UiText tag="h3" variant="subHeading" weight="bold">
                {t('profile.page.settings.hints.title')}
            </UiText>
            <div className={cls.hintsRow}>
                <div className={cls.settingsLabelWithHelp}>
                    <UiText>{t('profile.page.settings.hints.description')}</UiText>
                    <UITooltip
                        label={
                            <UiText variant="mediumPlain">
                                {t('profile.page.settings.hints.help')}
                            </UiText>
                        }
                        placement="bottom"
                    >
                        <UiButton
                            type="button"
                            variant="icon"
                            btnColor="ghost"
                            className={cls.helpButton}
                            startIcon={<Icon iconName="question" />}
                            aria-label={t('profile.page.settings.showHelp')}
                        />
                    </UITooltip>
                </div>
                <div className={cls.radioGroup} role="radiogroup">
                    <UiButton
                        type="button"
                        variant="text"
                        className={cls.radioOption}
                        startIcon={
                            <RadioBtn
                                isChecked={showHints}
                                onClickHandler={(event) => {
                                    event.stopPropagation();
                                    changeShowHints(true);
                                }}
                            />
                        }
                        btnText={t('profile.page.settings.hints.show')}
                        role="radio"
                        aria-checked={showHints}
                        disabled={isPending}
                        onClick={() => changeShowHints(true)}
                    />
                    <UiButton
                        type="button"
                        variant="text"
                        className={cls.radioOption}
                        startIcon={
                            <RadioBtn
                                isChecked={!showHints}
                                onClickHandler={(event) => {
                                    event.stopPropagation();
                                    changeShowHints(false);
                                }}
                            />
                        }
                        btnText={t('profile.page.settings.hints.hide')}
                        role="radio"
                        aria-checked={!showHints}
                        disabled={isPending}
                        onClick={() => changeShowHints(false)}
                    />
                </div>
            </div>
        </section>
    );
};
